import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import driveApi, { errorMessage } from '../../api/drive';
import { useDrive } from '../../contexts/DriveProvider';
import { auditActor, auditLabel, formatDateTime } from '../../lib/files';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const RULES = [
  {
    key: 'restricted',
    title: 'Stop inheriting access',
    help: 'Only people and teams added directly to this folder can open it. You stay in as manager.',
  },
  {
    key: 'no_external',
    title: 'Block external sharing',
    help: 'Hidden from every guest link, even when a parent folder is shared.',
  },
  {
    key: 'no_sync',
    title: 'Exclude from desktop sync',
    help: 'Web only — never stored on laptops.',
  },
];

const toCsv = (events) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = events.map((e) => [e.created_at, e.actor?.email || '', e.action, e.node_name, JSON.stringify(e.detail)].map(esc).join(','));
  return ['time,actor,action,item,detail', ...rows].join('\n');
};

const SecurityDialog = ({ item, onOpenChange }) => {
  const open = !!item;
  const { toast, bump } = useDrive();
  const [flags, setFlags] = useState({});
  const [events, setEvents] = useState(null);

  useEffect(() => {
    if (!item) return;
    setFlags({ restricted: item.restricted, no_external: item.no_external, no_sync: item.no_sync });
    setEvents(null);
    driveApi.audit({ node: item.id }).then(setEvents).catch(() => setEvents([]));
  }, [item]);

  const toggle = async (key) => {
    const next = !flags[key];
    setFlags((f) => ({ ...f, [key]: next }));
    try {
      const updated = await driveApi.security(item.id, { [key]: next });
      setFlags({ restricted: updated.restricted, no_external: updated.no_external, no_sync: updated.no_sync });
      bump();
    } catch (err) {
      setFlags((f) => ({ ...f, [key]: !next }));
      toast(errorMessage(err, 'Could not update security.'), 'error');
    }
  };

  const exportCsv = () => {
    const blob = new Blob([toCsv(events || [])], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-${item.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <span className="text-[13px] font-semibold text-muted-foreground">Folder security</span>
          <DialogTitle className="text-lg">{item?.name}</DialogTitle>
          <DialogDescription className="sr-only">Access rules and audit log for this folder.</DialogDescription>
        </DialogHeader>

        <div className="mt-5 flex flex-col gap-2.5">
          {RULES.map((rule) => (
            <label key={rule.key} className="flex cursor-pointer items-center gap-3 rounded-xl border border-border px-3.5 py-3 hover:bg-muted/50">
              <div className="flex flex-1 flex-col">
                <span className="font-semibold">{rule.title}</span>
                <span className="text-xs text-muted-foreground">{rule.help}</span>
              </div>
              <input
                type="checkbox"
                checked={!!flags[rule.key]}
                onChange={() => toggle(rule.key)}
                className="h-[22px] w-[22px] accent-[hsl(var(--primary))]"
              />
            </label>
          ))}
          <p className="text-xs text-muted-foreground">Turning on “Block external sharing” immediately switches off guest links to this folder and anything inside it. The desktop sync rule is saved now and applies once the desktop app ships.</p>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <b>Audit log</b>
          <button
            type="button"
            onClick={exportCsv}
            disabled={!events?.length}
            className="flex h-9 items-center gap-1.5 rounded-lg px-2 text-sm font-semibold text-primary hover:bg-accent disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
        <ul className="mt-2 flex max-h-64 flex-col overflow-y-auto text-sm">
          {events === null && <li className="py-3 text-muted-foreground">Loading…</li>}
          {events?.length === 0 && <li className="py-3 text-muted-foreground">No activity yet.</li>}
          {events?.map((e) => (
            <li key={e.id} className="flex gap-3 border-t border-muted py-2">
              <span className="w-36 shrink-0 tabular text-xs leading-5 text-muted-foreground">{formatDateTime(e.created_at)}</span>
              <span><b>{auditActor(e)}</b> {auditLabel(e.action)} “{e.node_name}”</span>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
};

export default SecurityDialog;
